import JSZip from 'jszip'

/* ============================================================
 * Film Notes · Export utilities
 * Fully client-side. No API keys, no servers.
 * ============================================================ */

// ---------- Design tokens (single source of truth) ----------
export const TOKENS = {
  color: {
    'film-red': '#C93B3B',
    'film-orange': '#FF8C42',
    'film-black': '#2B2B2B',
    'film-cream': '#F5F5F0',
    'film-gray': '#9B9B9B',
    'film-border': '#E8E8E8',
    'film-stored': '#F5E5E5',
    'film-nav': '#1A1A1A',
    'kodak-yellow': '#F0B92B',
    'safelight-amber': '#D9711F',
    'darkroom-burgundy': '#8B2E3F',
    'kodachrome-violet': '#5B3A82',
    'fuji-green': '#2D7A4F',
    'ektar-blue': '#2E5C8A',
  },
  font: {
    display: 'Futura PT',
    body: 'Helvetica Neue',
    mono: 'JetBrains Mono',
  },
  categoryColorMap: {
    CAT_COMMUNITY: '#5B3A82', // Kodachrome Violet
    CAT_LAB: '#2E5C8A', // Ektar Blue
    CAT_GEAR: '#2D7A4F', // Fuji Green
    CAT_FEATURE: '#8B2E3F', // Darkroom Burgundy
    CAT_TIP: '#D9711F', // Safelight Amber
    CAT_STOCK: '#F0B92B', // Kodak Yellow
  },
} as const

export interface FrameRecord {
  id: string
  name: string
  dims: string
  ratio: string
  cat: string
  page: string
}

// ---------- Tokens Studio for Figma format ----------
// Format: https://docs.tokens.studio/manage-settings/token-format
function buildTokensStudioJSON() {
  const colorTokens: Record<string, { value: string; type: string }> = {}
  Object.entries(TOKENS.color).forEach(([name, hex]) => {
    colorTokens[name] = { value: hex, type: 'color' }
  })

  return {
    global: {
      color: colorTokens,
      fontFamilies: {
        display: { value: TOKENS.font.display, type: 'fontFamilies' },
        body: { value: TOKENS.font.body, type: 'fontFamilies' },
        mono: { value: TOKENS.font.mono, type: 'fontFamilies' },
      },
      fontWeights: {
        bold: { value: '700', type: 'fontWeights' },
        regular: { value: '400', type: 'fontWeights' },
        medium: { value: '500', type: 'fontWeights' },
      },
      categoryColor: Object.fromEntries(
        Object.entries(TOKENS.categoryColorMap).map(([k, v]) => [
          k,
          { value: v, type: 'color' },
        ]),
      ),
    },
    $themes: [],
    $metadata: { tokenSetOrder: ['global'] },
  }
}

// ---------- CSS variables ----------
function buildCSSVariables() {
  const lines = [
    '/* Film Notes · Design Tokens',
    ' * Generated ' + new Date().toISOString().split('T')[0],
    ' */',
    '',
    ':root {',
  ]
  Object.entries(TOKENS.color).forEach(([k, v]) => {
    lines.push(`  --${k}: ${v};`)
  })
  lines.push('')
  Object.entries(TOKENS.categoryColorMap).forEach(([k, v]) => {
    lines.push(`  --${k.toLowerCase().replace(/_/g, '-')}-color: ${v};`)
  })
  lines.push('}')
  return lines.join('\n')
}

// ---------- Color swatch SVG ----------
function buildColorsSVG() {
  const entries = Object.entries(TOKENS.color)
  const sw = 200
  const sh = 240
  const cols = 4
  const rows = Math.ceil(entries.length / cols)
  const w = cols * sw
  const h = rows * sh + 80

  const swatches = entries
    .map(([name, hex], i) => {
      const col = i % cols
      const row = Math.floor(i / cols)
      const x = col * sw
      const y = row * sh + 80
      const isLight = ['film-cream', 'kodak-yellow', 'film-border'].includes(
        name,
      )
      const textColor = isLight ? '#2B2B2B' : '#FFFFFF'
      return `
        <g>
          <rect x="${x}" y="${y}" width="${sw}" height="${sh - 20}" fill="${hex}"/>
          <text x="${x + 16}" y="${y + sh - 60}" font-family="JetBrains Mono, monospace" font-size="13" fill="${textColor}">${name}</text>
          <text x="${x + 16}" y="${y + sh - 42}" font-family="JetBrains Mono, monospace" font-size="11" fill="${textColor}" opacity="0.6">${hex}</text>
        </g>`
    })
    .join('')

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <rect width="${w}" height="${h}" fill="#F5F5F0"/>
  <text x="40" y="48" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="28" fill="#2B2B2B">Film Notes · Color Tokens</text>
  ${swatches}
</svg>`
}

// ---------- Logo SVG (5 variants on one sheet) ----------
function buildLogoSheetSVG() {
  // Simple wordmark + monogram for each context
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
  <rect width="1200" height="800" fill="#F5F5F0"/>

  <!-- Variant 01 · Wordmark on cream -->
  <g transform="translate(60, 80)">
    <text x="0" y="40" font-family="JetBrains Mono, monospace" font-size="11" fill="#9B9B9B">01 · WORDMARK / CREAM</text>
    <text x="0" y="120" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="64" fill="#2B2B2B" letter-spacing="-2">Film Notes</text>
    <rect x="0" y="140" width="40" height="4" fill="#C93B3B"/>
  </g>

  <!-- Variant 02 · Wordmark on red -->
  <g transform="translate(620, 80)">
    <rect x="-40" y="-40" width="540" height="220" fill="#C93B3B"/>
    <text x="0" y="40" font-family="JetBrains Mono, monospace" font-size="11" fill="#FFFFFF" opacity="0.7">02 · WORDMARK / RED</text>
    <text x="0" y="120" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="64" fill="#FFFFFF" letter-spacing="-2">Film Notes</text>
    <rect x="0" y="140" width="40" height="4" fill="#FFFFFF"/>
  </g>

  <!-- Variant 03 · Monogram FN on black -->
  <g transform="translate(60, 360)">
    <rect x="-40" y="-40" width="240" height="240" fill="#2B2B2B"/>
    <text x="0" y="40" font-family="JetBrains Mono, monospace" font-size="11" fill="#FFFFFF" opacity="0.5">03 · MONOGRAM / BLACK</text>
    <text x="0" y="140" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="100" fill="#FFFFFF" letter-spacing="-4">FN</text>
  </g>

  <!-- Variant 04 · Monogram circle red -->
  <g transform="translate(380, 360)">
    <text x="0" y="0" font-family="JetBrains Mono, monospace" font-size="11" fill="#9B9B9B">04 · CIRCLE BADGE</text>
    <circle cx="80" cy="100" r="80" fill="#C93B3B"/>
    <text x="80" y="120" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="56" fill="#FFFFFF" text-anchor="middle">FN</text>
  </g>

  <!-- Variant 05 · Inline mono lockup -->
  <g transform="translate(620, 360)">
    <text x="0" y="0" font-family="JetBrains Mono, monospace" font-size="11" fill="#9B9B9B">05 · LOCKUP / MONO</text>
    <rect x="0" y="40" width="36" height="36" fill="#2B2B2B"/>
    <text x="18" y="66" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="22" fill="#FFFFFF" text-anchor="middle">FN</text>
    <text x="48" y="68" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="28" fill="#2B2B2B" letter-spacing="-0.5">Film Notes</text>
    <text x="48" y="92" font-family="JetBrains Mono, monospace" font-size="11" fill="#9B9B9B">For analog photographers · est. 2026</text>
  </g>

  <!-- Footer -->
  <text x="60" y="760" font-family="JetBrains Mono, monospace" font-size="11" fill="#9B9B9B">FILM NOTES · LOGO SET v1.0 · ${new Date().toISOString().split('T')[0]}</text>
</svg>`
}

// ---------- Frame manifest ----------
function buildFrameManifest(frames: FrameRecord[]) {
  return {
    version: '1.0',
    generated: new Date().toISOString(),
    project: 'Film Notes · Template Library',
    naming: {
      frame: 'CAT_{category} / FMT_{format} · {variant}',
      slide: 'FMT_CAROUSEL_{n}/{total}',
      print: 'FMT_PRINT / {piece} · {face}',
    },
    fonts: {
      display: TOKENS.font.display,
      body: TOKENS.font.body,
      mono: TOKENS.font.mono,
    },
    categoryColors: TOKENS.categoryColorMap,
    frames: frames.map((f) => ({
      id: f.id,
      name: f.name,
      dimensions: f.dims,
      aspectRatio: f.ratio,
      category: f.cat,
      sourcePage: f.page,
      categoryColor:
        TOKENS.categoryColorMap[
          `CAT_${f.cat}` as keyof typeof TOKENS.categoryColorMap
        ] || null,
    })),
    totals: {
      frames: frames.length,
      pages: new Set(frames.map((f) => f.page)).size,
      categories: new Set(frames.map((f) => f.cat)).size,
      ratios: new Set(frames.map((f) => f.ratio)).size,
    },
  }
}

// ---------- Frame thumbnail SVG (used as a placeholder in Figma) ----------
function buildFrameThumbSVG(f: FrameRecord) {
  const [w, h] = parseRatio(f.ratio)
  const color =
    TOKENS.categoryColorMap[
      `CAT_${f.cat}` as keyof typeof TOKENS.categoryColorMap
    ] || '#2B2B2B'

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <rect width="${w}" height="${h}" fill="#F5F5F0"/>
  <rect x="0" y="0" width="${Math.max(8, w * 0.012)}" height="${h}" fill="${color}"/>
  <text x="${w * 0.06}" y="${h * 0.1}" font-family="JetBrains Mono, monospace" font-size="${Math.round(h * 0.018)}" fill="#9B9B9B" text-transform="uppercase">${f.id}</text>
  <text x="${w * 0.06}" y="${h * 0.55}" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="${Math.round(h * 0.06)}" fill="#2B2B2B">${escapeXML(f.name)}</text>
  <text x="${w * 0.06}" y="${h * 0.62}" font-family="JetBrains Mono, monospace" font-size="${Math.round(h * 0.022)}" fill="#9B9B9B">${f.dims} · ${f.ratio}</text>
  <rect x="${w * 0.06}" y="${h * 0.85}" width="${w * 0.12}" height="${h * 0.025}" fill="${color}"/>
  <text x="${w * 0.2}" y="${h * 0.875}" font-family="JetBrains Mono, monospace" font-size="${Math.round(h * 0.022)}" fill="${color}" text-transform="uppercase">CAT_${f.cat}</text>
</svg>`
}

function parseRatio(ratio: string): [number, number] {
  // Handles "9/16", "1/1", "4/5", "148/210" etc.
  const parts = ratio.split('/').map((p) => parseFloat(p.trim()))
  if (parts.length !== 2 || parts.some(isNaN)) return [1080, 1080]
  const [a, b] = parts
  // Scale so the longer side is ~1080
  const max = Math.max(a, b)
  const scale = 1080 / max
  return [Math.round(a * scale), Math.round(b * scale)]
}

function escapeXML(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// ---------- README ----------
function buildFigmaReadme(frames: FrameRecord[]) {
  return `# Film Notes · Figma Import Bundle

Generated: ${new Date().toISOString().split('T')[0]}
Version: 1.0
Total frames: ${frames.length}

This bundle gives you everything needed to recreate the Film Notes Template Library inside Figma.

## What's inside

\`\`\`
/tokens.json           Design tokens (Tokens Studio for Figma format)
/manifest.json         Full frame inventory + naming + dimensions
/colors.css            CSS variables (drop into any web project)
/colors.svg            Color swatches sheet
/logo-set.svg          Five logo variants on one canvas
/frames/*.svg          One placeholder SVG per frame (correct ratio + category accent)
/IMPORT_INSTRUCTIONS.txt
\`\`\`

## Step-by-step Figma import

### 1. Install the fonts in Figma
- Futura PT — Bold / Heavy
- Helvetica Neue — Regular / Medium
- JetBrains Mono — Regular

In Figma → Account → Local fonts. Once installed they show up in any file.

### 2. Import design tokens (one-click)
1. In Figma, install the **Tokens Studio for Figma** plugin (free).
2. Open a new Figma file → Plugins → Tokens Studio.
3. In the plugin, click **Import** and drop \`tokens.json\` in.
4. You now have every color, font family, and category color as a token.

### 3. Bring in the logo + colors
- Drag \`logo-set.svg\` straight onto the canvas. Figma imports SVG as editable vector layers — ungroup and you have all 5 logo variants.
- Drag \`colors.svg\` onto a separate page named "00_README" as a reference sheet.

### 4. Build the frame inventory
- Drag each file from \`/frames/\` onto its respective page. They come in at the correct aspect ratio with the category accent and naming pre-baked.
- The frames are SVG placeholders — use them as guides. Replace content with your real designs, keeping the CAT_/FMT_ naming on the frame.

### 5. Naming convention (keep these intact)
- Frames: \`CAT_{category} / FMT_{format} · {variant}\`
- Carousel slides: \`FMT_CAROUSEL_{n}/{total}\`
- Print pieces: \`FMT_PRINT / {piece} · {face}\`

See \`manifest.json\` for the full list.

### Alternative · html.to.design plugin
If you want to capture the exact rendered designs from the web app:
1. Publish this Magic Patterns project to a public URL.
2. In Figma, install the **html.to.design** plugin.
3. Paste the URL — it walks the DOM and rebuilds Figma frames automatically.

## Category color mapping (social media only)

| Category | Token | Hex |
|----------|-------|-----|
| Community | kodachrome-violet | #5B3A82 |
| Labs | ektar-blue | #2E5C8A |
| Gear Info | fuji-green | #2D7A4F |
| Feature | darkroom-burgundy | #8B2E3F |
| PRO tips | safelight-amber | #D9711F |
| Film Stock Info | kodak-yellow | #F0B92B |

Tag chips, containers, and secondary buttons in the Brand Dossier are NOT remapped — they keep the system colors.

---

Film Notes · Template Library v1.0 · ${new Date().toISOString().split('T')[0]}
`
}

function buildAffinityReadme(frames: FrameRecord[]) {
  return `# Film Notes · Affinity Publisher Import Bundle

Generated: ${new Date().toISOString().split('T')[0]}
Total frames: ${frames.length}

Affinity Publisher 2 doesn't accept .fig or token JSON natively, so this bundle
gives you everything in formats Affinity reads first-class.

## Contents
- \`/colors.svg\` — drag onto a spread to pick swatches with the eyedropper
- \`/colors.css\` — token reference for engineering
- \`/colors-cmyk.txt\` — CMYK breakdowns for print pieces (lab cards, postcards)
- \`/logo-set.svg\` — vector logo set (import via File → Place)
- \`/frames/*.svg\` — placeholder SVG per frame at correct ratio
- \`/manifest.json\` — full frame inventory

## Setup

### 1. Install fonts (system-wide, then Affinity picks them up)
- Futura PT — Bold / Heavy
- Helvetica Neue — Regular / Medium
- JetBrains Mono — Regular

### 2. Create the global swatch palette
1. New Affinity Publisher document.
2. Window → Swatches → "Create palette from document" → name it "Film Notes".
3. File → Place → \`colors.svg\`. The fills auto-add to the palette.
4. For print pieces, manually add CMYK values from \`colors-cmyk.txt\`.

### 3. Set up spreads
- Use a separate Section / Spread per source page (02_FEED, 03_STORIES, 04_PRINT, 05_ADS).
- Drag the matching frame SVGs onto each spread as positional guides.
- Replace placeholder content with the real artwork — keep the CAT_/FMT_ names on each frame in the Layers panel.

### 4. Print pieces
Lab cards, stickers, and the postcard live in \`/frames/\`. Their dimensions are real-world mm. For CMYK output:
- Document → Convert format → CMYK/8 + ICC profile (FOGRA39 or U.S. Web Coated v2).
- Export → PDF/X-1a for press, PDF/X-4 for digital print.

Recipes & CMYK values are in \`colors-cmyk.txt\`.

---

Film Notes · Template Library v1.0
`
}

function buildCMYK() {
  return `Film Notes · CMYK breakdown

Use these for print pieces (lab cards, postcards, stickers).
For screen, stay with the RGB hex values in tokens.json.

PRIMARY
  film-red            #C93B3B   →   C 12 / M 88 / Y 75 / K 5
  film-cream          #F5F5F0   →   C 3  / M 2  / Y 6  / K 0
  film-black          #2B2B2B   →   C 0  / M 0  / Y 0  / K 90
  film-orange         #FF8C42   →   C 0  / M 56 / Y 78 / K 0

TERTIARY · SOCIAL CATEGORIES
  kodak-yellow        #F0B92B   →   C 4  / M 28 / Y 90 / K 0    (Film Stock Info)
  safelight-amber     #D9711F   →   C 8  / M 65 / Y 95 / K 0    (PRO Tips)
  darkroom-burgundy   #8B2E3F   →   C 35 / M 90 / Y 65 / K 30   (Feature)
  kodachrome-violet   #5B3A82   →   C 75 / M 85 / Y 15 / K 5    (Community)
  fuji-green          #2D7A4F   →   C 80 / M 25 / Y 80 / K 10   (Gear Info)
  ektar-blue          #2E5C8A   →   C 85 / M 60 / Y 15 / K 10   (Labs)

PAPER STOCKS
  Lab card · A6 / square / 4:5     Mohawk Superfine Eggshell 350gsm
  Lab card · A5 flyer              Munken Pure 240gsm
  Postcard                         GF Smith Colorplan Vellum White 270gsm
  Stickers · matte                 Vinyl matte white 100mic
  Stickers · clear                 Polypropylene clear 80mic
`
}

// ---------- Trigger download of any Blob ----------
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 200)
}

// ---------- Public API ----------

/**
 * Build and download the full Figma import bundle as a ZIP.
 * Includes tokens, manifest, color/logo SVGs, and per-frame placeholder SVGs.
 */
export async function exportFigmaBundle(frames: FrameRecord[]) {
  const zip = new JSZip()

  zip.file('tokens.json', JSON.stringify(buildTokensStudioJSON(), null, 2))
  zip.file('manifest.json', JSON.stringify(buildFrameManifest(frames), null, 2))
  zip.file('colors.css', buildCSSVariables())
  zip.file('colors.svg', buildColorsSVG())
  zip.file('logo-set.svg', buildLogoSheetSVG())
  zip.file('README.md', buildFigmaReadme(frames))
  zip.file('IMPORT_INSTRUCTIONS.txt', buildFigmaReadme(frames))

  const framesDir = zip.folder('frames')!
  frames.forEach((f) => {
    const safe = f.id.replace(/[\/\s]+/g, '_').replace(/[^A-Za-z0-9_·.-]/g, '')
    framesDir.file(`${safe}.svg`, buildFrameThumbSVG(f))
  })

  const blob = await zip.generateAsync({ type: 'blob' })
  downloadBlob(blob, `filmnotes-figma-bundle-v1.zip`)
}

/**
 * Build and download the Affinity Publisher bundle as a ZIP.
 */
export async function exportAffinityBundle(frames: FrameRecord[]) {
  const zip = new JSZip()

  zip.file('manifest.json', JSON.stringify(buildFrameManifest(frames), null, 2))
  zip.file('colors.css', buildCSSVariables())
  zip.file('colors.svg', buildColorsSVG())
  zip.file('colors-cmyk.txt', buildCMYK())
  zip.file('logo-set.svg', buildLogoSheetSVG())
  zip.file('README.md', buildAffinityReadme(frames))

  const framesDir = zip.folder('frames')!
  frames.forEach((f) => {
    const safe = f.id.replace(/[\/\s]+/g, '_').replace(/[^A-Za-z0-9_·.-]/g, '')
    framesDir.file(`${safe}.svg`, buildFrameThumbSVG(f))
  })

  const blob = await zip.generateAsync({ type: 'blob' })
  downloadBlob(blob, `filmnotes-affinity-bundle-v1.zip`)
}

/** Download just the design tokens JSON. */
export function exportTokensJSON() {
  const blob = new Blob([JSON.stringify(buildTokensStudioJSON(), null, 2)], {
    type: 'application/json',
  })
  downloadBlob(blob, 'filmnotes-tokens.json')
}

/** Download the logo set SVG. */
export function exportLogoSetSVG() {
  const blob = new Blob([buildLogoSheetSVG()], { type: 'image/svg+xml' })
  downloadBlob(blob, 'filmnotes-logo-set.svg')
}

/** Download a single SVG poster of all frame placeholders (PNG-like sheet). */
export async function exportPNGSheet(frames: FrameRecord[]) {
  // Build a contact-sheet SVG of all frame thumbs at a uniform grid.
  const cols = 4
  const cellW = 320
  const cellH = 420
  const rows = Math.ceil(frames.length / cols)
  const w = cols * cellW + 80
  const h = rows * cellH + 160

  const cells = frames
    .map((f, i) => {
      const col = i % cols
      const row = Math.floor(i / cols)
      const x = 40 + col * cellW
      const y = 120 + row * cellH
      const [fw, fh] = parseRatio(f.ratio)
      const maxArt = 240
      const aspect = fw / fh
      let aw = maxArt
      let ah = maxArt
      if (aspect >= 1) ah = maxArt / aspect
      else aw = maxArt * aspect
      const ax = x + (cellW - aw) / 2
      const ay = y + (260 - ah) / 2

      const color =
        TOKENS.categoryColorMap[
          `CAT_${f.cat}` as keyof typeof TOKENS.categoryColorMap
        ] || '#2B2B2B'

      return `
        <g>
          <rect x="${ax}" y="${ay}" width="${aw}" height="${ah}" fill="#F5F5F0" stroke="#E8E8E8"/>
          <rect x="${ax}" y="${ay}" width="6" height="${ah}" fill="${color}"/>
          <text x="${ax + 16}" y="${ay + ah - 18}" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="13" fill="#2B2B2B">${escapeXML(f.name)}</text>
          <text x="${x + 12}" y="${y + 300}" font-family="JetBrains Mono, monospace" font-size="10" fill="#9B9B9B">${escapeXML(f.id)}</text>
          <text x="${x + 12}" y="${y + 316}" font-family="JetBrains Mono, monospace" font-size="10" fill="#2B2B2B">${f.dims} · ${f.ratio}</text>
          <rect x="${x + 12}" y="${y + 332}" width="44" height="14" fill="${color}"/>
          <text x="${x + 34}" y="${y + 343}" font-family="JetBrains Mono, monospace" font-size="9" fill="#FFFFFF" text-anchor="middle">${f.cat}</text>
        </g>`
    })
    .join('')

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <rect width="${w}" height="${h}" fill="#FFFFFF"/>
  <text x="40" y="60" font-family="Futura PT, Arial Black, sans-serif" font-weight="700" font-size="34" fill="#2B2B2B">Film Notes · Master Contact Sheet</text>
  <text x="40" y="86" font-family="JetBrains Mono, monospace" font-size="12" fill="#9B9B9B">${frames.length} FRAMES · GENERATED ${new Date().toISOString().split('T')[0]}</text>
  ${cells}
</svg>`

  const blob = new Blob([svg], { type: 'image/svg+xml' })
  downloadBlob(blob, 'filmnotes-contact-sheet.svg')
}

/** Download a print pack ZIP: print-only frames + CMYK guide + print specs. */
export async function exportPrintPack(frames: FrameRecord[]) {
  const zip = new JSZip()
  const printFrames = frames.filter((f) => f.page === '04_PRINT')

  zip.file('colors-cmyk.txt', buildCMYK())
  zip.file(
    'manifest.json',
    JSON.stringify(buildFrameManifest(printFrames), null, 2),
  )
  zip.file(
    'README.txt',
    `Film Notes · Print Pack
${printFrames.length} pieces · CMYK-ready

Open in Affinity Publisher 2 or Adobe InDesign.
See colors-cmyk.txt for ink breakdowns and paper recipes.

Set document color profile to CMYK/8 + FOGRA39 (Europe) or U.S. Web Coated v2 (US).
Export PDF/X-1a for press, PDF/X-4 for digital print.
`,
  )

  const dir = zip.folder('print-frames')!
  printFrames.forEach((f) => {
    const safe = f.id.replace(/[\/\s]+/g, '_').replace(/[^A-Za-z0-9_·.-]/g, '')
    dir.file(`${safe}.svg`, buildFrameThumbSVG(f))
  })

  const blob = await zip.generateAsync({ type: 'blob' })
  downloadBlob(blob, 'filmnotes-print-pack.zip')
}
